<app-page-shell [breadcrumb]="breadcrumb" width="wide">
  <header class="mx-auto max-w-3xl px-6 pt-12 pb-14 text-center md:pt-16 md:pb-20">
    <h1
      class="hero-title font-display text-4xl font-bold leading-[1.06] text-foreground md:text-[3.25rem]"
    >
      <span class="hero-title-accent font-medium italic text-primary">
        {{ 'pages.reports.hero.title' | transloco }}
      </span>
    </h1>
    <p
      class="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[17px]"
    >
      {{ 'pages.reports.hero.description' | transloco }}
    </p>
  </header>

  <div
    class="mx-auto mb-12 flex max-w-xl items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30"
  >
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="h-4 w-4 text-muted-foreground"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
    <input
      type="text"
      [value]="query()"
      (input)="updateQuery($event)"
      [placeholder]="'pages.reports.searchPlaceholder' | transloco"
      [attr.aria-label]="'pages.reports.searchLabel' | transloco"
      class="input-unstyled min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-foreground shadow-none outline-none ring-0 placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-0 focus-visible:shadow-none"
    />
    @if (query()) {
      <button
        type="button"
        (click)="query.set('')"
        [attr.aria-label]="'pages.reports.clearSearch' | transloco"
        class="rounded-full p-1 text-muted-foreground hover:text-foreground"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          class="h-3.5 w-3.5"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    }
  </div>

  <div class="mx-auto mb-12 max-w-4xl">
    <div class="flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        (click)="activeTemas.set([])"
        [attr.aria-pressed]="activeTemas().length === 0"
        class="rounded-full border px-3 py-1 text-xs font-medium transition"
        [class.border-primary]="activeTemas().length === 0"
        [class.bg-primary]="activeTemas().length === 0"
        [class.text-primary-foreground]="activeTemas().length === 0"
        [class.border-border]="activeTemas().length > 0"
        [class.bg-card]="activeTemas().length > 0"
        [class.text-muted-foreground]="activeTemas().length > 0"
      >
        {{ 'pages.reports.allThemes' | transloco }}
      </button>

      @for (tema of temas; track tema.id) {
        @if (temaCount(tema.id) > 0) {
          <button
            type="button"
            (click)="toggleTema(tema.id)"
            [attr.aria-pressed]="isTemaActive(tema.id)"
            [title]="tema.descriptionKey | transloco"
            class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition"
            [class.border-primary]="isTemaActive(tema.id)"
            [class.bg-primary]="isTemaActive(tema.id)"
            [class.text-primary-foreground]="isTemaActive(tema.id)"
            [class.border-border]="!isTemaActive(tema.id)"
            [class.bg-card]="!isTemaActive(tema.id)"
            [class.text-muted-foreground]="!isTemaActive(tema.id)"
          >
            {{ tema.labelKey | transloco }}
            <span
              class="text-[10px] tabular-nums"
              [class.text-primary-foreground/75]="isTemaActive(tema.id)"
              [class.text-muted-foreground/60]="!isTemaActive(tema.id)"
            >
              {{ temaCount(tema.id) }}
            </span>
          </button>
        }
      }
    </div>
  </div>

  @if (lead && !isFiltering()) {
    <article class="-mx-6 rounded-3xl bg-primary/6 px-8 py-12 md:-mx-10 md:px-12 md:py-14">
      <div class="grid gap-x-10 gap-y-8 md:grid-cols-12">
        <aside class="md:col-span-3">
          <div class="mx-auto w-40 md:w-full">
            <div
              aria-hidden="true"
              class="relative flex aspect-3/4 w-full flex-col justify-between overflow-hidden rounded-2xl bg-gradient-arp p-5 text-white shadow-elevated"
            >
              <div class="eyebrow-on-dark">CGDados · Enap</div>
              <div class="space-y-2">
                <div class="h-px w-10 bg-primary-glow"></div>
                <p
                  class="font-display text-[15px] font-bold leading-[1.15] tracking-[-0.01em] text-white"
                >
                  {{ lead.titleKey | transloco }}
                </p>
                <p class="eyebrow-on-dark">{{ 'pages.reports.report' | transloco }}</p>
              </div>
            </div>
          </div>
          <div class="mt-5 space-y-1.5 text-xs leading-relaxed text-muted-foreground">
            <p class="tabular-nums">{{ lead.dateKey | transloco }}</p>
            @if (lead.pages) {
              <p class="tabular-nums">
                {{ lead.pages }} {{ 'pages.reports.pages' | transloco }}
              </p>
            }
            @if (lead.partnerKey) {
              <p>
                {{ 'pages.reports.partnership' | transloco }}:
                {{ lead.partnerKey | transloco }}
              </p>
            }
          </div>
        </aside>

        <div class="md:col-span-9">
          <p class="eyebrow">{{ 'pages.reports.latest' | transloco }}</p>
          <h2
            class="mt-3 font-display text-3xl font-semibold leading-[1.1] tracking-[-0.02em] text-foreground md:text-4xl"
          >
            {{ lead.titleKey | transloco }}
          </h2>
          <p
            class="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[17px]"
          >
            {{ lead.descriptionKey | transloco }}
          </p>
          <div class="mt-7 flex flex-wrap items-center gap-3">
            <a
              [href]="lead.pdfUrl"
              target="_blank"
              rel="noreferrer"
              class="btn-pill btn-pill--primary group"
            >
              {{ 'pages.reports.access' | transloco }}
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>
            <a
              [href]="lead.pdfUrl"
              target="_blank"
              rel="noreferrer"
              class="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground/80 transition hover:border-primary/60 hover:text-primary"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-3.5 w-3.5"
              >
                <path d="M12 3v12m-5-5 5 5 5-5M5 21h14" />
              </svg>
              {{ 'pages.reports.download' | transloco }}
            </a>
          </div>
        </div>
      </div>
    </article>
  }

  @if (filtered().length === 0) {
    <div class="mt-16 py-16 text-center">
      <p class="font-display text-lg font-semibold text-foreground">
        {{ 'pages.reports.empty.title' | transloco }}
      </p>
      <p class="mt-2 text-sm text-muted-foreground">
        {{ 'pages.reports.empty.description' | transloco }}
        <button
          type="button"
          (click)="clearFilters()"
          class="text-primary underline-offset-4 hover:underline"
        >
          {{ 'pages.reports.empty.clear' | transloco }}
        </button>.
      </p>
    </div>
  } @else {
    <section aria-labelledby="reports-index-title" class="mt-16">
      <header class="mb-2 flex flex-wrap items-end justify-between gap-x-8 gap-y-3 pb-4">
        <div>
          <h2 id="reports-index-title" class="eyebrow">
            {{
              (isFiltering() ? 'pages.reports.results' : 'pages.reports.moreReports') | transloco
            }}
          </h2>
          <p class="mt-1 text-sm text-muted-foreground tabular-nums">
            {{ listReports().length }}
            {{
              (listReports().length === 1
                ? 'pages.reports.reportCountSingular'
                : 'pages.reports.reportCountPlural'
              ) | transloco
            }}
            @if (isFiltering() && query().trim()) {
              {{ 'pages.reports.forQuery' | transloco }} “{{ query().trim() }}”
            }
            @if (isFiltering() && activeTemas().length > 0) {
              {{ 'pages.reports.inThemes' | transloco }}
              @for (tema of activeTemas(); track tema; let last = $last) {
                {{ temaLabel(tema) }}{{ last ? '' : ', ' }}
              }
            }
          </p>
        </div>
      </header>

      <ol class="divide-y divide-foreground/10 border-t border-foreground/10">
        @for (relatorio of listReports(); track relatorio.slug) {
          <li>
            <a
              [href]="relatorio.pdfUrl"
              target="_blank"
              rel="noreferrer"
              class="group grid grid-cols-12 items-start gap-x-10 gap-y-3 py-8 transition focus-visible:outline-none"
            >
              <div class="col-span-12 space-y-2 md:col-span-3">
                <p class="text-[13px] leading-relaxed text-muted-foreground tabular-nums">
                  {{ relatorio.dateKey | transloco }}
                </p>
                <p class="text-[13px] leading-relaxed text-muted-foreground">
                  {{ 'pages.reports.authorship' | transloco }}:
                  {{ relatorio.authorsKey | transloco }}
                </p>
                @if (relatorio.pages) {
                  <p
                    class="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground tabular-nums"
                  >
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="h-3.5 w-3.5"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <path d="M14 2v6h6M8 13h8M8 17h8" />
                    </svg>
                    {{ relatorio.pages }} {{ 'pages.reports.pages' | transloco }}
                  </p>
                }
              </div>

              <div class="col-span-12 space-y-3 md:col-span-9">
                <h3
                  class="font-display text-xl font-bold leading-[1.2] tracking-[-0.015em] text-foreground transition-colors group-hover:text-secondary md:text-[22px]"
                >
                  {{ relatorio.titleKey | transloco }}
                </h3>
                <p class="text-[15px] leading-[1.65] text-muted-foreground">
                  {{ relatorio.descriptionKey | transloco }}
                </p>
                <span
                  class="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary transition-colors group-hover:text-secondary/80"
                >
                  {{ 'pages.reports.access' | transloco }}
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="h-3.5 w-3.5"
                  >
                    <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  </svg>
                </span>
              </div>
            </a>
          </li>
        }
      </ol>
    </section>
  }

  <section class="mt-16">
    <div
      class="relative overflow-hidden rounded-3xl bg-gradient-arp px-8 py-12 text-center text-white shadow-elevated md:px-12 md:py-14"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/5 blur-2xl"
      ></div>
      <div class="relative">
        <p class="mb-3 eyebrow-on-dark">{{ 'pages.reports.promo.eyebrow' | transloco }}</p>
        <h2 class="font-display text-2xl font-bold leading-tight md:text-3xl">
          <span class="text-white">Data</span>
          <span class="italic text-primary-glow">Stories</span>
        </h2>
        <p class="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/75 md:text-base">
          {{ 'pages.reports.promo.description' | transloco }}
        </p>
        <a
          [routerLink]="'/datastories' | localizedRouterLink"
          class="btn-pill btn-pill--primary mt-7"
        >
          {{ 'pages.reports.promo.cta' | transloco }}
        </a>
      </div>
    </div>
  </section>
</app-page-shell>
